import { useEffect, useMemo, useRef, useState } from 'react';
import { EnvironmentOutlined, GiftOutlined, SearchOutlined } from '@ant-design/icons';
import { Pagination, Select } from 'antd';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { readWalletAmounts } from '../Wallet/walletData';
import ExploreMore from './components/ExploreMore';
import ProductDetail from './components/ProductDetail';
import PurchaseModal from './components/PurchaseModal';
import {
    getAvailableDenoms,
    getProductDetailPath,
    getProductDisplayName,
    getProductLogo,
    type CatalogProduct,
} from './catalog';
import '../Home/Home.css';
import './Explore.css';
import { AppApc } from '../../api/appApc';
import { useDebounce } from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../i18n/format';

type ApiCategory = {
    id: string;
    name?: string;
    nameEn?: string;
    names?: Partial<Record<string, string>>;
};

type ApiCatalog = ApiCategory;

type ApiRegion = {
    id: string;
    name?: string;
    nameEn?: string;
    nameCn?: string;
    nameVi?: string;
    nameTh?: string;
    nameJa?: string;
    nameKo?: string;
    nameId?: string;
    names?: Partial<Record<string, string>>;
};

type ProductsMeta = {
    limit: number;
    page: number;
    total: number;
    totalPages: number;
};

const PAGE_SIZE = 6;
const GIFT_CARD_CATALOG_ID = 'gift_card';

function formatCategoryLabel(value?: string) {
    if (!value) return '';

    return value
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();
}

function getApiLanguageKey(language: string) {
    if (language.startsWith('zh')) return 'zh';
    return language.split('-')[0];
}

const REGION_NAME_FIELDS: Record<string, keyof ApiRegion> = {
    en: 'nameEn',
    zh: 'nameCn',
    vi: 'nameVi',
    th: 'nameTh',
    ja: 'nameJa',
    ko: 'nameKo',
    id: 'nameId',
};

function getLocalizedRegionName(region?: ApiRegion, language?: string) {
    if (!region) return '';

    const languageKey = getApiLanguageKey(language || '');
    const field = REGION_NAME_FIELDS[languageKey];
    const specificName = field ? region[field] : undefined;

    return (
        region.names?.[languageKey] ||
        (typeof specificName === 'string' ? specificName : '') ||
        region.names?.en ||
        region.nameEn ||
        region.name ||
        region.id
    );
}

function getLocalizedCategoryName(category?: ApiCategory, language?: string) {
    if (!category) return '';

    const languageKey = getApiLanguageKey(language || '');
    return (
        category.names?.[languageKey] ||
        category.names?.en ||
        category.nameEn ||
        category.name ||
        formatCategoryLabel(category.id)
    );
}

function readProductPayload(payload: unknown): CatalogProduct | null {
    if (!payload || typeof payload !== 'object') return null;
    const record = payload as { data?: unknown; id?: string };
    if (record.data && typeof record.data === 'object' && 'id' in record.data) {
        return record.data as CatalogProduct;
    }
    return typeof record.id === 'string' && record.id ? (record as CatalogProduct) : null;
}

function readLocatedProduct(state: unknown): CatalogProduct | null {
    if (!state || typeof state !== 'object' || !('product' in state)) return null;
    const product = (state as { product?: CatalogProduct }).product;
    return product?.id ? product : null;
}

function ExplorePage() {
    const { t, i18n } = useTranslation('explore');
    const language = i18n.resolvedLanguage || i18n.language;
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const detailId = searchParams.get('id')?.trim() || '';
    const locatedProduct = readLocatedProduct(location.state);
    const wallet = readWalletAmounts(user);
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounce(query, 500);
    const appliedSearch = useRef(debouncedQuery);
    const [regionId, setRegionId] = useState('all');
    const [categoryId, setCategoryId] = useState('all');
    const [detailProduct, setDetailProduct] = useState<CatalogProduct | null>(
        locatedProduct?.id === detailId ? locatedProduct : null
    );
    const [detailLoading, setDetailLoading] = useState(
        Boolean(detailId) && locatedProduct?.id !== detailId
    );
    const [detailRequest, setDetailRequest] = useState(detailId);
    const [review, setReview] = useState<{
        product: CatalogProduct;
        amount: number;
        quantity: number;
    } | null>(null);

    if (detailRequest !== detailId) {
        setDetailRequest(detailId);
        if (locatedProduct?.id === detailId) {
            setDetailProduct(locatedProduct);
            setDetailLoading(false);
        } else if (!detailId || detailProduct?.id !== detailId) {
            setDetailProduct(null);
            setDetailLoading(Boolean(detailId));
        }
        setReview(null);
    }
    const [categories, setCategories] = useState<ApiCategory[]>([]);
    const [regions, setRegions] = useState<ApiRegion[]>([]);
    const [products, setProducts] = useState<CatalogProduct[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [regionsLoading, setRegionsLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState<ProductsMeta>({
        limit: PAGE_SIZE,
        page: 1,
        total: 0,
        totalPages: 0,
    });
    const [catalogs, setCatalogs] = useState<ApiCatalog[]>([]);
    const [catalogId, setCatalogId] = useState('');
    const [catalogsLoading, setCatalogsLoading] = useState(true);

    const fetchCategories = async () => {
        setCategoriesLoading(true);
        try {
            const response = await AppApc.GetCategories();
            if (response.status === 200) {
                setCategories(Array.isArray(response.data?.data) ? response.data.data : []);
            }
        } catch (error) {
            console.error('Error fetching categories', error);
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const fetchRegions = async () => {
        setRegionsLoading(true);
        try {
            const response = await AppApc.GetRegions();
            if (response.status === 200) {
                const nextRegions = Array.isArray(response.data?.data) ? response.data.data : [];
                setRegions(nextRegions);
                if (nextRegions.some((item: ApiRegion) => item.id === 'all')) {
                    setRegionId('all');
                } else if (nextRegions[0]?.id) {
                    setRegionId(nextRegions[0].id);
                }
            }
        } catch (error) {
            console.error('Error fetching regions', error);
            setRegions([]);
        } finally {
            setRegionsLoading(false);
        }
    };

    const fetchCatalogs = async () => {
        setCatalogsLoading(true);
        try {
            const response = await AppApc.GetCatalogs();
            if (response.status === 200) {
                const nextCatalogs: ApiCatalog[] = Array.isArray(response.data?.data)
                    ? response.data.data
                    : [];
                setCatalogs(nextCatalogs);
                setCatalogId((current) =>
                    nextCatalogs.some((item) => item.id === current)
                        ? current
                        : nextCatalogs[0]?.id || ''
                );
            }
        } catch (error) {
            console.error('Error fetching catalogs', error);
            setCatalogs([]);
        } finally {
            setCatalogsLoading(false);
        }
    };

    const fetchProducts = async (nextPage: number) => {
        setProductsLoading(true);
        try {
            const response = await AppApc.GetProducts(
                catalogId === GIFT_CARD_CATALOG_ID ? categoryId : 'all',
                regionId,
                debouncedQuery,
                nextPage,
                PAGE_SIZE,
                catalogId || undefined
            );
            if (response.status === 200) {
                const payload = response.data;
                const nextMeta = payload?.meta || {};
                setProducts(Array.isArray(payload?.data) ? payload.data : []);
                setMeta({
                    limit: Number(nextMeta.limit) || PAGE_SIZE,
                    page: Number(nextMeta.page) || nextPage,
                    total: Number(nextMeta.total ?? payload?.count) || 0,
                    totalPages: Number(nextMeta.totalPages) || 0,
                });
            }
        } catch (error) {
            console.error('Error fetching products', error);
            setProducts([]);
            setMeta({
                limit: PAGE_SIZE,
                page: nextPage,
                total: 0,
                totalPages: 0,
            });
        } finally {
            setProductsLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchRegions();
        fetchCatalogs();
    }, []);

    useEffect(() => {
        if (detailId || catalogsLoading) return;
        if (catalogs.length > 0 && !catalogId) return;

        const searchChanged = appliedSearch.current !== debouncedQuery;
        appliedSearch.current = debouncedQuery;

        if (searchChanged && page !== 1) {
            setPage(1);
            return;
        }

        fetchProducts(page);
    }, [page, categoryId, regionId, debouncedQuery, detailId, catalogId, catalogsLoading]);

    useEffect(() => {
        if (!detailId) {
            setDetailProduct(null);
            setDetailLoading(false);
            setReview(null);
            return;
        }

        if (detailProduct?.id === detailId) {
            setDetailLoading(false);
            return;
        }

        let cancelled = false;

        const loadDetail = async () => {
            setDetailLoading(true);
            try {
                if (locatedProduct?.id === detailId) {
                    setDetailProduct(locatedProduct);
                    return;
                }

                try {
                    const byId = await AppApc.GetProduct(detailId);
                    if (cancelled) return;
                    const product = readProductPayload(byId.data);
                    if (product?.id === detailId) {
                        setDetailProduct(product);
                        return;
                    }
                } catch {
                    // products/:id may not exist; fall back to the catalog list
                }

                const response = await AppApc.GetProducts(
                    'all',
                    'all',
                    '',
                    1,
                    48,
                    catalogId || undefined
                );
                if (cancelled) return;
                const list: CatalogProduct[] = Array.isArray(response.data?.data)
                    ? response.data.data
                    : [];
                setDetailProduct(list.find((item) => item.id === detailId) || null);
            } catch (error) {
                console.error('Error fetching product', error);
                if (!cancelled) setDetailProduct(null);
            } finally {
                if (!cancelled) setDetailLoading(false);
            }
        };

        loadDetail();

        return () => {
            cancelled = true;
        };
    }, [detailId, locatedProduct]);

    const activeCategory = useMemo(
        () => categories.find((item) => item.id === categoryId) || categories[0],
        [categories, categoryId]
    );

    const activeCatalog = useMemo(
        () => catalogs.find((item) => item.id === catalogId) || catalogs[0],
        [catalogs, catalogId]
    );

    const resultsTitle =
        catalogId === GIFT_CARD_CATALOG_ID && categoryId && categoryId !== 'all'
            ? getLocalizedCategoryName(activeCategory, language)
            : getLocalizedCategoryName(activeCatalog, language) || t('results.title');

    const activeRegion = useMemo(
        () => regions.find((item) => item.id === regionId) || regions[0],
        [regionId, regions]
    );

    const regionLabel = getLocalizedRegionName(activeRegion, language) || t('filters.allRegions');

    const resolveProductRegion = (product?: CatalogProduct | null) => {
        if (!product) return regionLabel;
        const match = regions.find((item) => item.id === product.region);
        return getLocalizedRegionName(match, language) || product.regionName || product.country || regionLabel;
    };

    const getProductCategoryLabel = (category?: string) => {
        if (!category) return '';

        const matchedCategory = categories.find(
            (item) => item.id === category || item.nameEn === category || item.name === category
        );

        return getLocalizedCategoryName(matchedCategory, language) || formatCategoryLabel(category);
    };

    const resetToFirstPage = () => {
        setPage(1);
    };

    const openProduct = (product: CatalogProduct) => {
        setDetailProduct(product);
        setDetailLoading(false);
        navigate(getProductDetailPath(product));
    };

    const closeDetail = () => {
        setReview(null);
        navigate('/explore');
    };

    if (detailId) {
        return (
            <div className="explore-page">
                {detailLoading ? (
                    <div
                        className="explore-detail"
                        aria-busy="true"
                        aria-label={t('loading.giftCard')}
                    >
                        <span className="explore-skel explore-detail__skel-back" />
                        <div className="explore-detail__layout">
                            <section className="explore-detail__stage" aria-hidden>
                                <div className="explore-detail__brand">
                                    <span className="explore-skel explore-detail__skel-logo" />
                                    <div className="explore-detail__intro">
                                        <span className="explore-skel explore-detail__skel-tag" />
                                        <span className="explore-skel explore-detail__skel-title" />
                                        <span className="explore-skel explore-detail__skel-region" />
                                    </div>
                                </div>
                                <span className="explore-skel explore-detail__skel-line" />
                                <span className="explore-skel explore-detail__skel-line explore-detail__skel-line--short" />
                                <div className="explore-detail__facts">
                                    <span className="explore-skel explore-detail__skel-fact" />
                                    <span className="explore-skel explore-detail__skel-fact" />
                                    <span className="explore-skel explore-detail__skel-fact" />
                                </div>
                            </section>
                            <aside className="explore-detail__panel" aria-hidden>
                                <span className="explore-skel explore-detail__skel-heading" />
                                <span className="explore-skel explore-detail__skel-copy" />
                                <div className="explore-detail__denoms">
                                    {Array.from({ length: 4 }).map((_, index) => (
                                        <span
                                            key={index}
                                            className="explore-skel explore-detail__skel-denom"
                                        />
                                    ))}
                                </div>
                                <span className="explore-skel explore-detail__skel-total" />
                                <span className="explore-skel explore-detail__skel-button" />
                            </aside>
                        </div>
                    </div>
                ) : detailProduct ? (
                    <ProductDetail
                        product={detailProduct}
                        regionLabel={resolveProductRegion(detailProduct)}
                        onBack={closeDetail}
                        onReview={(amount, quantity) =>
                            setReview({ product: detailProduct, amount, quantity })
                        }
                    />
                ) : (
                    <div className="explore-detail">
                        <button type="button" className="explore-detail__back" onClick={closeDetail}>
                            {t('actions.allGiftCards')}
                        </button>
                        <div className="explore-page__empty">
                            <h3>{t('empty.detailTitle')}</h3>
                            <p>{t('empty.detailDescription')}</p>
                        </div>
                    </div>
                )}

                <PurchaseModal
                    key={
                        review
                            ? `${review.product.id}-${review.amount}-${review.quantity}`
                            : 'closed'
                    }
                    product={review?.product ?? null}
                    initialStep="review"
                    initialAmount={review?.amount}
                    initialQuantity={review?.quantity}
                    country={resolveProductRegion(review?.product)}
                    availableCredits={wallet.availableCredits}
                    displayName={
                        typeof user?.displayName === 'string'
                            ? user.displayName
                            : typeof user?.name === 'string'
                              ? user.name
                              : undefined
                    }
                    recipientEmail={
                        typeof user?.email === 'string' ? user.email : undefined
                    }
                    onClose={() => setReview(null)}
                />
            </div>
        );
    }

    return (
        <div className="explore-page">
            <div className="page-heading">
                <div>
                    <h1>{t('heading.title')}</h1>
                    <p>{t('heading.description')}</p>
                </div>
                <Link className="explore-page__vouchers" to="/vouchers">
                    <GiftOutlined />
                    {t('actions.myVouchers')}
                </Link>
            </div>

            <div
                className="explore-page__catalog-tabs"
                role="tablist"
                aria-label={t('filters.catalogsAria')}
                aria-busy={catalogsLoading}
            >
                {catalogsLoading
                    ? [128, 72, 148].map((width, index) => (
                          <span
                              key={index}
                              className="explore-skel explore-skel--catalog-tab"
                              style={{ width }}
                              aria-hidden
                          />
                      ))
                    : catalogs.map((item) => {
                          const label = getLocalizedCategoryName(item, language);
                          const isActive = catalogId === item.id;

                          return (
                              <button
                                  key={item.id}
                                  type="button"
                                  role="tab"
                                  className={`explore-page__catalog-tab${isActive ? ' active' : ''}`}
                                  aria-selected={isActive}
                                  onClick={() => {
                                      if (item.id === catalogId) return;
                                      setCatalogId(item.id);
                                      setCategoryId('all');
                                      resetToFirstPage();
                                  }}
                              >
                                  {label}
                              </button>
                          );
                      })}
            </div>

            <div className="explore-page__filters">
                <label className="explore-page__search">
                    <SearchOutlined />
                    <input
                        aria-label={t('filters.searchAria')}
                        placeholder={t('filters.searchPlaceholder')}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </label>
                <Select
                    className="explore-page__select"
                    popupClassName="explore-page__select-dropdown"
                    aria-label={t('filters.regionAria')}
                    value={regionId}
                    loading={regionsLoading}
                    disabled={regionsLoading}
                    onChange={(value) => {
                        setRegionId(value);
                        resetToFirstPage();
                    }}
                    suffixIcon={<EnvironmentOutlined />}
                    options={regions.map((item) => ({
                        value: item.id,
                        label: getLocalizedRegionName(item, language),
                    }))}
                    placeholder={t('filters.selectRegion')}
                />
            </div>

            {catalogId === GIFT_CARD_CATALOG_ID ? (
                <div
                    className="explore-page__tabs"
                    role="tablist"
                    aria-label={t('filters.categoriesAria')}
                    aria-busy={categoriesLoading}
                >
                    {categoriesLoading
                        ? [72, 96, 64, 110, 84, 78, 100, 68].map((width, index) => (
                              <span
                                  key={index}
                                  className="explore-skel explore-skel--tab"
                                  style={{ width }}
                                  aria-hidden
                              />
                          ))
                        : categories.map((item) => {
                              const label = getLocalizedCategoryName(item, language);
                              const isActive = categoryId === item.id;

                              return (
                                  <button
                                      key={item.id}
                                      type="button"
                                      className={`explore-page__tab${isActive ? ' active' : ''}`}
                                      aria-pressed={isActive}
                                      onClick={() => {
                                          setCategoryId(item.id);
                                          resetToFirstPage();
                                      }}
                                  >
                                      {label}
                                  </button>
                              );
                          })}
                </div>
            ) : null}

            <div className="section-head">
                <h2>{resultsTitle}</h2>
                <small>
                    {productsLoading ? (
                        <span className="explore-skel explore-skel--count" aria-hidden />
                    ) : (
                        <>
                            {t('results.count', {
                                count: meta.total,
                                formattedCount: formatNumber(meta.total),
                            })}
                        </>
                    )}
                </small>
            </div>

            <div
                className="catalog explore-page__catalog"
                aria-busy={productsLoading}
                aria-label={productsLoading ? t('loading.giftCards') : undefined}
            >
                {productsLoading
                    ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
                          <div
                              className="product-card explore-card explore-card--skeleton"
                              key={`skel-${index}`}
                              aria-hidden
                          >
                              <div className="explore-card__media">
                                  <div className="explore-skel explore-skel--logo" />
                              </div>
                              <div className="explore-card__body">
                                  <span className="explore-skel explore-skel--title" />
                                  <span className="explore-skel explore-skel--meta" />
                                  <div className="explore-card__footer">
                                      <span className="explore-skel explore-skel--from" />
                                      <span className="explore-skel explore-skel--cta" />
                                  </div>
                              </div>
                          </div>
                      ))
                    : products.length
                      ? products.map((product: CatalogProduct) => {
                            const displayName = getProductDisplayName(product);
                            const logoUrl = getProductLogo(product);
                            const productRegion = resolveProductRegion(product);
                            const firstDenom = getAvailableDenoms(product)[0];
                            const value = firstDenom?.value;
                            const currency = firstDenom?.currency || product.currency || '';
                            const formattedValue = value == null ? null : formatNumber(value);
                            const categoryLabel = getProductCategoryLabel(product.category);
                            const isInstant = product.deliveryType === 'INSTANT';

                            return (
                                <button
                                    type="button"
                                    className="product-card explore-card"
                                    key={product.id}
                                    aria-label={t('product.viewAria', { productName: displayName })}
                                    onClick={() => openProduct(product)}
                                >
                                    <div className="explore-card__media">
                                        <div className="explore-card__glow" aria-hidden />
                                        {isInstant ? (
                                            <span className="explore-card__badge">
                                                {t('product.instant')}
                                            </span>
                                        ) : null}
                                        <div
                                            className={`explore-card__logo${
                                                logoUrl ? ' explore-card__logo--image' : ''
                                            }`}
                                        >
                                            {logoUrl ? (
                                                <img src={logoUrl} alt="" loading="lazy" />
                                            ) : (
                                                <span>{displayName.charAt(0)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="explore-card__body">
                                        <strong className="explore-card__name">{displayName}</strong>
                                        <span className="explore-card__meta">
                                            {productRegion}
                                            {categoryLabel ? ` · ${categoryLabel}` : ''}
                                        </span>
                                        <div className="explore-card__footer">
                                            <span className="explore-card__from">
                                                {t('product.from')}{' '}
                                                <em>
                                                    {formattedValue != null
                                                        ? `${formattedValue} ${currency}`
                                                        : '—'}
                                                </em>
                                            </span>
                                            <span className="explore-card__cta" aria-hidden>
                                                {t('actions.buy')}
                                            </span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                      : (
                            <div className="explore-page__empty">
                                <h3>{t('empty.resultsTitle')}</h3>
                                <p>{t('empty.resultsDescription')}</p>
                                <button
                                    type="button"
                                    className="explore-page__vouchers"
                                    onClick={() => {
                                        setQuery('');
                                        setCategoryId('all');
                                        resetToFirstPage();
                                    }}
                                >
                                    {t('actions.clearSearch')}
                                </button>
                            </div>
                        )}
            </div>

            {!productsLoading && meta.total > 0 ? (
                <div className="explore-page__pagination">
                    <Pagination
                        current={meta.page || page}
                        pageSize={meta.limit || PAGE_SIZE}
                        total={meta.total}
                        showSizeChanger={false}
                        hideOnSinglePage
                        onChange={(nextPage) => setPage(nextPage)}
                    />
                </div>
            ) : null}

            <ExploreMore />
        </div>
    );
}

export default ExplorePage;
